import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api.ts';
import { Markdown } from '../components/Markdown.tsx';
import { ArticleCard } from '../components/ArticleCard.tsx';
import { Comments } from '../components/Comments.tsx';
import type { ArticleFull, ArticleSummary } from '../../../shared/types.ts';
import type { AuthUser } from '../hooks/useApp.ts';

const CAT_LABEL: Record<string, string> = {
  tutorial: 'Tutorial',
  project: 'Project',
  curiosity: 'Curiosity',
  qa: 'Q&A',
  cheatsheet: 'Cheatsheet',
  library: 'Library',
};

interface Props {
  user: AuthUser | null;
  onRequestSignIn: () => void;
}

export function ArticlePage({ user, onRequestSignIn }: Props): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleFull | null>(null);
  const [related, setRelated] = useState<ArticleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setArticle(null);
    setError(null);
    api.getArticle(slug)
      .then(d => { setArticle(d.article); setRelated(d.related); window.scrollTo({ top: 0 }); })
      .catch(e => setError(e instanceof Error ? e.message : 'not found'));
  }, [slug]);

  useEffect(() => {
    if (article) document.title = `${article.title} — How to Python`;
    return () => { document.title = 'How to Python — tutorials, projects, curiosities'; };
  }, [article]);

  if (error) return <div className="center-pad">{error}. <Link to="/">Back home</Link>.</div>;
  if (!article) return <div className="center-pad"><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div className="page">
      <article className={`article cat-${article.category}`}>
        <div className="crumbs">
          <Link to="/">Home</Link> / <Link to={`/category/${article.category}`}>{CAT_LABEL[article.category]}</Link>
        </div>
        <h1>{article.title}</h1>
        <div className="article-meta">
          <span className="badge" style={{ color: `var(--cat-${article.category})` }}>{CAT_LABEL[article.category]}</span>
          <span>{article.difficulty}</span>
          <span>{article.reading_minutes} min read</span>
          {article.published_at && <span>published {article.published_at}</span>}
          {article.tags.map(t => <span key={t}>#{t}</span>)}
        </div>
        <Markdown source={article.body_md} />
      </article>

      {related.length > 0 && (
        <div style={{ maxWidth: 760, margin: '48px auto 0' }}>
          <div className="section-head">
            <h2>Related reading</h2>
          </div>
          <div className="article-grid">
            {related.map(a => <ArticleCard key={a.slug} a={a} />)}
          </div>
        </div>
      )}

      <Comments slug={article.slug} user={user} onRequestSignIn={onRequestSignIn} />
    </div>
  );
}
